import { isAxiosError } from "axios"
import { AuthRepository } from "@/repositories/auth-repository"
import { User } from "@/models/user.model"
import { getToken } from "@clerk/vue"
import { isProductionEnvironment } from "@/helper/app/browser-helper"

const DEV_CURRENT_USER_RETRY_DELAY_MS = 3001
const DEV_CURRENT_USER_MAX_ATTEMPTS = 10

function isTransientCurrentUserError(error: unknown): boolean {
  if (!isAxiosError(error)) {
    return true
  }
  if (!error.response) {
    return true
  }
  return [408, 429, 502, 503, 504].includes(error.response.status)
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export class AuthController {
  private authRepository: AuthRepository = new AuthRepository()

  public async acceptInvite(invitationCode: string): Promise<User | undefined> {
    const result = await this.authRepository.acceptInvite(invitationCode)
    if (result.status === 200) {
      return new User(result.data)
    } else if (result.status === 404) {
      throw Error("De uitnodigingscode is ongeldig of verlopen.")
    } else if (result.status === 409) {
      throw Error(
        "De uitnodiging kon niet worden geaccepteerd omdat er al een account is geregistreerd met deze credentials."
      )
    } else {
      throw Error(
        "Er ging iets mis bij het accepteren van de uitnodiging. Probeer het later opnieuw."
      )
    }
  }

  public async getDjangoUser(): Promise<User | null> {
    // In local/dev, retry while the backend is still starting so the loading
    // screen (awaiting initialize → current-user) stays up instead of flashing empty.
    const maxAttempts = isProductionEnvironment() ? 1 : DEV_CURRENT_USER_MAX_ATTEMPTS

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await this.authRepository.getDjangoUser()
      } catch (error) {
        const shouldRetry =
          !isProductionEnvironment() && attempt < maxAttempts && isTransientCurrentUserError(error)

        if (!shouldRetry) {
          console.log(error)
          return null
        }

        await sleep(DEV_CURRENT_USER_RETRY_DELAY_MS)
      }
    }

    return null
  }

  public static async getAccessToken(): Promise<string | null> {
    try {
      let token = await getToken({ template: "jwt" })
      if (token && this._isExpiringSoon(token)) {
        token = await getToken({ template: "jwt", skipCache: true })
      }
      if (token) {
        const secure = isProductionEnvironment() ? "Secure;" : ""
        document.cookie = `clerk_jwt=${token}; path=/; ${secure} SameSite=Strict; max-age=3600`
        return `Bearer ${token}`
      }
      console.log("No active session found")
      return null
    } catch (e) {
      console.log("Failed to get token from Clerk", e)
      return null
    }
  }

  private static _isExpiringSoon(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]))
      return payload.exp * 1000 - Date.now() < 60_000
    } catch {
      return true
    }
  }

  public static async validateInviteCode(invitationCode: string): Promise<boolean> {
    const result = await new AuthRepository().validateInviteCode(invitationCode)
    return result.status === 200
  }
}
